import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { compose } from "redux"
import * as classnames from "classnames"

import JourneyRouter from "containers/journey-router"
import ShowPick from "containers/pages/pick-summaries/show-pick"
import ShowBothPicks from "containers/pages/pick-summaries/show-both-picks"
import Output from "containers/output"
import { DelayedSpinner as Spinner } from "components/shared/spinner"
import ExperimentContainer from "components/app/experiment-container"

import { withDebugConditionSelector } from "./hoc/withDebugConditionSelector"

import {
  loadPlansForCondition,
  loadPlansForPickedConditions,
  loadBenefits
} from "redux/actions/plan-actions"
import { loadDevices } from "redux/actions/device-actions"
import { loadExtras } from "redux/actions/extras-actions"
import { selectCondition, setStoreName } from "redux/actions/condition-actions"
import { mutateData } from "redux/actions/mutation-actions"
import {
  createLoadingSelector,
  createErrorSelector
} from "redux/selectors/shared/loading-selectors"
import * as ACTIONS from "redux/actions/types"
import { useEFSData } from "hooks/efs-input"
import { BT_retention_ruleset } from "mutation-rules"

import "App.scss"
import { selectDevice } from "redux/actions/basket-actions"
import ShowRandomCacheOutput from "components/debug/ShowRandomCache"
import { null_ruleset } from "./mutation-rules"
import { PropTypes } from "prop-types"

// The main app class
// This loads in the initial data for the pages to then use
const App = ({
  isFetching,
  fetchErrors,
  submit,
  loadPickedPlans,
  loadPlans,
  loadBenefits,
  loadDevices,
  loadExtras,
  selectCondition,
  setStoreName,
  mutateData,
  selectDevice
}) => {
  const [isFetched, setIsFetched] = useState(false)
  const [isMutated, setIsMutated] = useState(false)

  const efsdata = useEFSData()
  let {
    condition,
    showPicked,
    showDoublePicked,
    pickedCondition,
    picked2Condition,
    storeName
  } = efsdata

  // Effect to load in the relevant data
  useEffect(() => {
    // If it's a show_picked condition, we want to load in plans for
    // all the conditions seen

    if (showPicked) {
      let conditions = [pickedCondition]

      if (showDoublePicked) {
        conditions.push(picked2Condition)
      }
      // Let's load in the data for this condition
      loadPickedPlans(conditions, storeName, efsdata)
    } else {
      // otherwise load in the plans for a single condition
      // and set the condition in the redux store

      // Let's load in the data for this condition
      loadPlans(storeName, condition, efsdata)
      selectCondition(condition)
    }
    loadBenefits()
    loadDevices()
    loadExtras(storeName)

    setStoreName(storeName)

    let deviceType

    switch (storeName) {
      case "BT_Acquisition_FTTC":
      case "BT_Acquisition_FTTP":
      case "BT_Retention_FTTC":
      case "BT_Retention_FTTP":
        deviceType = "BT_ROUTER"
        break
      case "Sky_Acquisition_FTTC":
      case "Sky_Acquisition_FTTP":
      case "Sky_Retention_FTTC":
      case "Sky_Retention_FTTP":
        deviceType = "SKY_ROUTER"
        break
      case "Vodafone_Acquisition_FTTC":
      case "Vodafone_Acquisition_FTTP":
      case "Vodafone_Retention_FTTC":
      case "Vodafone_Retention_FTTP":
        deviceType = "VODAFONE_ROUTER"
        break
      case "Virgin_Acquisition_FTTC":
      case "Virgin_Acquisition_FTTP":
      case "Virgin_Retention_FTTC":
      case "Virgin_Retention_FTTP":
        deviceType = "VIRGIN_ROUTER"
        break
      default:
        throw Error("No Storename mapping to device!!!")
    }

    selectDevice(deviceType)

    setIsFetched(true)
  }, [
    condition,
    efsdata,
    loadBenefits,
    loadDevices,
    loadExtras,
    loadPickedPlans,
    loadPlans,
    mutateData,
    picked2Condition,
    pickedCondition,
    selectCondition,
    selectDevice,
    setStoreName,
    showDoublePicked,
    showPicked,
    storeName
  ])

  // Effect to run mutations from the appropriate ruleset
  useEffect(() => {
    if (!isMutated && !isFetching && isFetched) {
      const rulesByStoreName = {
        BT_Acquisition_FTTC: null_ruleset,
        BT_Acquisition_FTTP: null_ruleset,
        BT_Retention_FTTC: BT_retention_ruleset,
        BT_Retention_FTTP: BT_retention_ruleset,
        Vodafone_Acquisition_FTTC: null_ruleset,
        Vodafone_Acquisition_FTTP: null_ruleset,
        Vodafone_Retention_FTTC: null_ruleset,
        Vodafone_Retention_FTTP: null_ruleset,
        Virgin_Acquisition_FTTC: null_ruleset,
        Virgin_Acquisition_FTTP: null_ruleset,
        Virgin_Retention_FTTC: null_ruleset,
        Virgin_Retention_FTTP: null_ruleset,
        Sky_Acquisition_FTTC: null_ruleset,
        Sky_Acquisition_FTTP: null_ruleset,
        Sky_Retention_FTTC: null_ruleset,
        Sky_Retention_FTTP: null_ruleset
      }

      // Hack to stop trying to mutate if we're summarizing the data
      if (!showPicked) {
        // Hey Spuds - time to mutate some data!
        // A cookie to anyone who gets that reference
        mutateData(rulesByStoreName[storeName], { efsdata })
      }

      setIsMutated(true)
    }
  }, [
    efsdata,
    isFetched,
    isFetching,
    isMutated,
    mutateData,
    showPicked,
    storeName
  ])

  const renderErrors = errors => <div className="error">Error: {errors}</div>

  // Display a spinner if we're waiting on files to load
  if (!isMutated || isFetching || !isFetched) return <Spinner />

  // If there's any errors, then display then
  if (fetchErrors) return renderErrors(fetchErrors)

  if (showPicked) {
    return showDoublePicked ? <ShowBothPicks /> : <ShowPick />
  }

  return (
    <div
      className={classnames(
        "ReactApp",
        {
          "bt-store":
            storeName === "BT_Acquisition_FTTC" ||
            storeName === "BT_Acquisition_FTTP" ||
            storeName === "BT_Retention_FTTC" ||
            storeName === "BT_Retention_FTTP"
        },
        {
          "vodafone-store":
            storeName === "Vodafone_Acquisition_FTTC" ||
            storeName === "Vodafone_Acquisition_FTTP" ||
            storeName === "Vodafone_Retention_FTTC" ||
            storeName === "Vodafone_Retention_FTTP"
        },
        {
          "virgin-store":
            storeName === "Virgin_Acquisition_FTTC" ||
            storeName === "Virgin_Acquisition_FTTP" ||
            storeName === "Virgin_Retention_FTTC" ||
            storeName === "Virgin_Retention_FTTP"
        },
        {
          "sky-store":
            storeName === "Sky_Acquisition_FTTC" ||
            storeName === "Sky_Acquisition_FTTP" ||
            storeName === "Sky_Retention_FTTC" ||
            storeName === "Sky_Retention_FTTP"
        }
      )}
    >
      {/* <StoreLabel store={storeName} /> */}
      <ExperimentContainer>
        <JourneyRouter submit={submit} storeName={storeName} />
      </ExperimentContainer>
      <Output />
      <ShowRandomCacheOutput />
    </div>
  )
}

// Get a basic loading selector to get the state of the files we're fetching
const filesToLoad = [
  ACTIONS.FILES.PLANS,
  ACTIONS.FILES.DEVICES,
  ACTIONS.FILES.BENEFITS,
  ACTIONS.FILES.EXTRAS,
  ACTIONS.FILES.PICKED_PLANS
]
const loadingSelector = createLoadingSelector(filesToLoad)
const errorSelector = createErrorSelector(filesToLoad)

const mapStateToProps = state => ({
  isFetching: loadingSelector(state),
  fetchErrors: errorSelector(state)
})

const mapDispatchToProps = dispatch => ({
  selectCondition: condition => dispatch(selectCondition(condition)),
  loadPlans: (storeName, condition, efsdata) =>
    dispatch(loadPlansForCondition(storeName, condition, efsdata)),
  loadPickedPlans: (conditions, storeName, efsdata) =>
    dispatch(loadPlansForPickedConditions(conditions, storeName, efsdata)),
  loadBenefits: () => dispatch(loadBenefits),
  loadDevices: () => dispatch(loadDevices()),
  loadExtras: storeName => dispatch(loadExtras(storeName)),
  setStoreName: storeName => dispatch(setStoreName(storeName)),
  mutateData: (mutationRules, globals) =>
    dispatch(mutateData(mutationRules, globals)),
  selectDevice: deviceID => dispatch(selectDevice(deviceID))
})

App.propTypes = {
  isFetching: PropTypes.bool,
  fetchErrors: PropTypes.string,
  submit: PropTypes.func,
  loadPickedPlans: PropTypes.func,
  loadPlans: PropTypes.func,
  loadBenefits: PropTypes.func,
  loadDevices: PropTypes.func,
  loadExtras: PropTypes.func,
  selectCondition: PropTypes.func,
  setStoreName: PropTypes.func,
  mutateData: PropTypes.func,
  selectDevice: PropTypes.func
}

export default compose(
  withDebugConditionSelector,
  connect(mapStateToProps, mapDispatchToProps)
)(App)
